import React, { useState, useEffect } from 'react';
import { Shield, X, ExternalLink, Eye, Database, Share2 } from 'lucide-react';
import firebaseService from '../services/firebaseService';

export default function PrivacyBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('fiskelykke-privacy-consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      setAccepted(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('fiskelykke-privacy-consent', JSON.stringify({
      accepted: true,
      timestamp: Date.now(),
      version: '1.0'
    }));
    setAccepted(true);
    setShowBanner(false);
    // Start Google Analytics nå som samtykke er gitt (laster GA + cookie)
    firebaseService.initAnalytics();
  };

  const handleReject = () => {
    // Lagre avslag — appen fungerer fullt ut, men uten analyse/cookie
    localStorage.setItem('fiskelykke-privacy-consent', JSON.stringify({
      accepted: false,
      timestamp: Date.now(),
      version: '1.0'
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Privacy Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[500] bg-[#3B4D3C] text-white p-6 shadow-2xl border-t-4 border-[#E67E22]">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4">
            <Shield className="text-[#E67E22] mt-1 flex-shrink-0" size={24} />
            <div className="flex-1">
              <h3 className="font-serif font-bold text-xl mb-2">Personvern og Cookies</h3>
              <p className="text-white/90 mb-4 leading-relaxed">
                FiskDa bruker informasjonskapsler (Google Analytics) for å forstå hvor mange
                som bruker siden. Dine fangstdata lagres alltid trygt uansett valg. Velger du
                "Avvis", fungerer appen helt som normalt — vi teller deg bare ikke i statistikken.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleAccept}
                  className="bg-[#E67E22] text-white px-6 py-2 rounded font-bold hover:bg-[#D35400] transition-colors"
                >
                  Godta alle
                </button>
                <button
                  onClick={handleReject}
                  className="bg-white/10 text-white px-6 py-2 rounded font-bold hover:bg-white/20 transition-colors"
                >
                  Avvis
                </button>
                <button
                  onClick={() => setShowDetails(true)}
                  className="text-[#E67E22] hover:text-white transition-colors flex items-center gap-1"
                >
                  Les mer <ExternalLink size={16} />
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowBanner(false)}
              className="text-white/60 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Detailed Privacy Modal */}
      {showDetails && (
        <PrivacyDetailsModal onClose={() => setShowDetails(false)} onAccept={handleAccept} />
      )}
    </>
  );
}

function PrivacyDetailsModal({ onClose, onAccept }) {
  return (
    <div className="fixed inset-0 z-[600] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <h2 className="font-serif text-2xl font-bold text-[#3B4D3C]">Personvernerklæring</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6 space-y-8">
          {/* Databehandling */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Database className="text-[#E67E22]" size={24} />
              <h3 className="font-serif text-xl font-bold text-[#3B4D3C]">Hvilke data samler vi inn?</h3>
            </div>
            <div className="bg-[#F8F5F0] p-4 rounded-lg space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-[#D1CCC0]">
                <span className="font-medium">Fangstbilder</span>
                <span className="text-sm text-gray-600">Lagres kryptert i Firebase</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#D1CCC0]">
                <span className="font-medium">Posisjon (valgfri)</span>
                <span className="text-sm text-gray-600">3 personvernnivåer</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#D1CCC0]">
                <span className="font-medium">Fangstdetaljer</span>
                <span className="text-sm text-gray-600">Art, vekt, dato, værdata</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="font-medium">Bruker-ID</span>
                <span className="text-sm text-gray-600">Anonym Firebase-ID</span>
              </div>
            </div>
          </section>

          {/* Personvern */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Eye className="text-[#E67E22]" size={24} />
              <h3 className="font-serif text-xl font-bold text-[#3B4D3C]">Ditt personvern</h3>
            </div>
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <h4 className="font-bold text-green-800 mb-2">✅ Vi gjør ALDRI:</h4>
                <ul className="text-green-700 space-y-1 text-sm">
                  <li>• Selger dine data til tredjeparter</li>
                  <li>• Deler nøyaktige GPS-koordinater uten samtykke</li>
                  <li>• Bruker data til reklame eller profilering</li>
                  <li>• Lagrer sensitive personopplysninger</li>
                </ul>
              </div>
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h4 className="font-bold text-blue-800 mb-2">🔒 Vi gjør:</h4>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• Krypterer all data i transit og hvile</li>
                  <li>• Lar deg kontrollere personvernnivå per fangst</li>
                  <li>• Gir deg full kontroll over deling</li>
                  <li>• Sletter data permanent når du ber om det</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Deling */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Share2 className="text-[#E67E22]" size={24} />
              <h3 className="font-serif text-xl font-bold text-[#3B4D3C]">Deling av fangster</h3>
            </div>
            <div className="bg-[#F8F5F0] p-4 rounded-lg">
              <p className="mb-3">Når du deler en fangst:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#E67E22] font-bold">•</span>
                  <span>Kun data du har valgt å dele blir synlig</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#E67E22] font-bold">•</span>
                  <span>Nøyaktige koordinater erstattes med maskert område</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#E67E22] font-bold">•</span>
                  <span>Private notater fjernes automatisk</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#E67E22] font-bold">•</span>
                  <span>Delte fangster utløper etter 30 dager</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Rettigheter */}
          <section>
            <h3 className="font-serif text-xl font-bold text-[#3B4D3C] mb-4">Dine rettigheter (GDPR)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white border border-[#D1CCC0] p-4 rounded-lg">
                <h4 className="font-bold mb-2">📋 Innsyn</h4>
                <p className="text-sm text-gray-600">Se alle data vi har om deg</p>
              </div>
              <div className="bg-white border border-[#D1CCC0] p-4 rounded-lg">
                <h4 className="font-bold mb-2">✏️ Retting</h4>
                <p className="text-sm text-gray-600">Endre eller oppdatere dine data</p>
              </div>
              <div className="bg-white border border-[#D1CCC0] p-4 rounded-lg">
                <h4 className="font-bold mb-2">🗑️ Sletting</h4>
                <p className="text-sm text-gray-600">Slett alle dine data permanent</p>
              </div>
              <div className="bg-white border border-[#D1CCC0] p-4 rounded-lg">
                <h4 className="font-bold mb-2">📦 Portabilitet</h4>
                <p className="text-sm text-gray-600">Eksporter dine data</p>
              </div>
            </div>
          </section>

          {/* Kontakt */}
          <section className="bg-[#3B4D3C] text-white p-6 rounded-lg">
            <h3 className="font-serif text-xl font-bold mb-4">Kontakt oss</h3>
            <p className="mb-4">
              Har du spørsmål om personvern eller ønsker å utøve dine rettigheter?
            </p>
            <div className="space-y-2 text-sm">
              <p><strong>E-post:</strong> tmfoght@gmail.com</p>
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 flex gap-4">
          <button
            onClick={onAccept}
            className="flex-1 bg-[#E67E22] text-white py-3 rounded font-bold hover:bg-[#D35400] transition-colors"
          >
            Godta og fortsett
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 rounded font-bold hover:bg-gray-50 transition-colors"
          >
            Lukk
          </button>
        </div>
      </div>
    </div>
  );
}